import { useEffect, useMemo, useState } from 'react';
import PageSeo from '@/seo/PageSeo';
import { CalendarDaysIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Loader from '@/components/Common/Loader';
import { useEvents } from '@/hooks/useEvents';
import useText from '@/hooks/useText';
import { sendGAPageview } from '../utils/gaConsent';
import {
  filterEvents,
  splitEventsByTime,
  uniqueSorted,
  type EventCardItem,
  type EventTimeframe,
} from './events/eventsHelpers';
import InsightsHero from './insights/InsightsHero';
import InsightsCard from './insights/InsightsCard';
import { isEventsEnabled } from '@/config/events';
import NotFoundContent from '@/pages/NotFound';
import './insights/insights.css';

const EMPTY_FILTERS = {
  query: '',
  eventType: 'all',
  place: 'all',
  year: 'all',
  timeframe: 'all' as EventTimeframe,
};

function EventCard({ event }: { event: EventCardItem }) {
  return (
    <InsightsCard
      variant="events"
      href={event.link}
      external={Boolean(event.link)}
      media={
        event.thumbnailUrl ? (
          <img src={event.thumbnailUrl} alt={event.thumbnailAlt} className="insights-card__image" />
        ) : (
          <div className="insights-card__placeholder" aria-hidden>
            <CalendarDaysIcon className="insights-card__placeholder-icon" />
          </div>
        )
      }
      badge={event.eventType}
      title={event.title}
      description={event.description}
      metaLeft={event.dateLabel || 'Date TBA'}
      metaRight={event.place || (event.link ? 'Open' : undefined)}
    />
  );
}

const EventsContent = () => {
  const eventsTitle = useText('events.title');
  const eventsSubtitle = useText('events.subtitle');
  const { events, loading, error } = useEvents();
  const [query, setQuery] = useState(EMPTY_FILTERS.query);
  const [eventType, setEventType] = useState(EMPTY_FILTERS.eventType);
  const [place, setPlace] = useState(EMPTY_FILTERS.place);
  const [year, setYear] = useState(EMPTY_FILTERS.year);
  const [timeframe, setTimeframe] = useState<EventTimeframe>(EMPTY_FILTERS.timeframe);

  useEffect(() => {
    sendGAPageview({ hitType: 'pageview', page: '/events', title: 'Events' });
  }, []);

  const eventTypes = useMemo(() => uniqueSorted(events.map(event => event.eventType)), [events]);
  const places = useMemo(() => uniqueSorted(events.map(event => event.place)), [events]);
  const years = useMemo(
    () => uniqueSorted(events.map(event => event.year)).sort((a, b) => Number(b) - Number(a)),
    [events],
  );

  const filteredEvents = useMemo(
    () => filterEvents(events, { query, eventType, place, year, timeframe }),
    [events, query, eventType, place, year, timeframe],
  );

  const { upcoming, recent } = useMemo(() => splitEventsByTime(filteredEvents), [filteredEvents]);

  const hasActiveFilters =
    query.trim() !== '' || eventType !== 'all' || place !== 'all' || year !== 'all' || timeframe !== 'all';

  const showUpcoming = timeframe !== 'past';
  const showRecent = timeframe !== 'upcoming';

  const clearFilters = () => {
    setQuery(EMPTY_FILTERS.query);
    setEventType(EMPTY_FILTERS.eventType);
    setPlace(EMPTY_FILTERS.place);
    setYear(EMPTY_FILTERS.year);
    setTimeframe(EMPTY_FILTERS.timeframe);
  };

  return (
    <>
      <PageSeo
        title="Events and Webinars | DISGENET"
        description="Upcoming DISGENET webinars, conferences and events on gene-disease associations, target identification and precision medicine."
        path="/events"
      />

      <div className="insights-page">
        <InsightsHero variant="events" eyebrow="Insights" title={eventsTitle} subtitle={eventsSubtitle}>
          <label className="insights-search">
            <span className="sr-only">Search events</span>
            <MagnifyingGlassIcon className="insights-search__icon" aria-hidden />
            <input
              type="search"
              className="insights-search__input"
              placeholder="Search events"
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          </label>
          <label>
            <span className="sr-only">Filter by type</span>
            <select
              className="insights-pill"
              value={eventType}
              onChange={event => setEventType(event.target.value)}
            >
              <option value="all">All types</option>
              {eventTypes.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr-only">Filter by location</span>
            <select className="insights-pill" value={place} onChange={event => setPlace(event.target.value)}>
              <option value="all">All locations</option>
              {places.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr-only">Filter by year</span>
            <select className="insights-pill" value={year} onChange={event => setYear(event.target.value)}>
              <option value="all">All years</option>
              {years.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr-only">Filter by date</span>
            <select
              className="insights-pill"
              value={timeframe}
              onChange={event => setTimeframe(event.target.value as EventTimeframe)}
            >
              <option value="all">All dates</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
            </select>
          </label>
          {hasActiveFilters ? (
            <button type="button" className="insights-clear" onClick={clearFilters}>
              Clear
            </button>
          ) : null}
        </InsightsHero>

        <div className="insights-content">
          {loading ? (
            <div className="py-16">
              <Loader text="Loading events..." isInnerSpinner />
            </div>
          ) : null}

          {!loading && error ? <p className="insights-error">{error}</p> : null}

          {!loading && !error && events.length === 0 ? (
            <p className="insights-empty">No events are available at the moment.</p>
          ) : null}

          {!loading && !error && events.length > 0 && filteredEvents.length === 0 ? (
            <p className="insights-empty">No events match the selected filters.</p>
          ) : null}

          {!loading && !error && filteredEvents.length > 0 ? (
            <>
              {showUpcoming && upcoming.length > 0 ? (
                <section className="insights-section" aria-label="Upcoming events">
                  <div className="insights-grid">
                    {upcoming.map(event => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </div>
                </section>
              ) : null}

              {showUpcoming && upcoming.length === 0 && timeframe === 'upcoming' ? (
                <p className="insights-empty">No upcoming events.</p>
              ) : null}

              {showRecent && recent.length > 0 ? (
                <section className="insights-section" aria-labelledby="recent-events-heading">
                  <h2 id="recent-events-heading" className="insights-section__title">
                    Recent events
                  </h2>
                  <div className="insights-grid">
                    {recent.map(event => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

const Events = () => {
  if (!isEventsEnabled) return <NotFoundContent />;
  return <EventsContent />;
};

export default Events;
